// ============================================================================
// BOT DETECTION SERVICE - Action Timing Analysis
// ============================================================================
// This service tracks player action timings and detects patterns
// that may indicate bot usage (inhuman reaction times, perfect timing).
// Creates FLAGS for human review - NO auto-bans.
// ============================================================================

import {
  collection,
  doc,
  setDoc,
  getDoc,
  updateDoc,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import {
  ActionTimingRecord,
  ActionTiming,
  TimingStats,
  FLAG_THRESHOLDS,
} from '../../types/securityTypes';
import securityFlagService from './securityFlagService';

// ============================================================================
// CONSTANTS
// ============================================================================

const TIMINGS_COLLECTION = 'actionTimings';

// Timing thresholds (in milliseconds)
const INSTANT_ACTION_MS = 500;          // Actions under 500ms = instant
const SUSPICIOUS_FAST_MS = 200;         // Actions under 200ms = suspiciously fast
const HUMAN_MIN_REACTION_MS = 100;      // Fastest human reaction
const ROBOT_CONSISTENT_VARIANCE = 50;   // Variance under 50ms = too consistent

// Analysis parameters
const MAX_ACTIONS_STORED = 500;         // Rolling window of actions
const MIN_ACTIONS_FOR_ANALYSIS = 50;    // Need at least 50 actions
const ANALYSIS_FREQUENCY = 50;          // Analyze every 50 actions
const SUSPICIOUS_INSTANT_RATE = 0.3;    // 30%+ instant actions = suspicious
const SUSPICIOUS_FAST_RATE = 0.1;       // 10%+ under 200ms = suspicious

// Decision complexity affects expected think time
const EXPECTED_THINK_TIMES: { [key: string]: { min: number; max: number } } = {
  fold: { min: 500, max: 5000 },
  check: { min: 300, max: 3000 },
  call: { min: 1000, max: 10000 },
  raise: { min: 2000, max: 30000 },
  'all-in': { min: 3000, max: 60000 },
};

// ============================================================================
// BOT DETECTION SERVICE CLASS
// ============================================================================

class BotDetectionService {
  // ============================================================================
  // ACTION TIMING RECORDING
  // ============================================================================

  /**
   * Record an action timing
   * Called from gameEngine when player takes action
   */
  async recordActionTiming(
    tableId: string,
    handNumber: number,
    playerId: string,
    actionType: string,
    thinkTimeMs: number,
    preActionPotSize?: number
  ): Promise<void> {
    try {
      console.log(`⏱️ [BotDetect] Recording action: ${playerId.substring(0, 8)} ${actionType} in ${thinkTimeMs}ms`);

      // Create action record
      const action: ActionTiming = {
        tableId,
        handNumber,
        actionType,
        thinkTimeMs,
        timestamp: Timestamp.now(),
        preActionPotSize,
        decisionComplexity: this.getDecisionComplexity(actionType, thinkTimeMs, preActionPotSize),
      };

      // Get or create player's timing record
      const timingRef = doc(db, TIMINGS_COLLECTION, playerId);
      const timingDoc = await getDoc(timingRef);

      if (timingDoc.exists()) {
        const record = timingDoc.data() as ActionTimingRecord;
        
        // Add new action to rolling window
        const recentActions = [...record.recentActions, action];
        
        // Trim to max size
        while (recentActions.length > MAX_ACTIONS_STORED) {
          recentActions.shift();
        }

        // Recalculate stats
        const stats = this.calculateTimingStats(recentActions);

        await updateDoc(timingRef, {
          recentActions,
          stats,
          lastAnalyzed: Timestamp.now(),
        });

        // Check if we should run full analysis
        if (recentActions.length % ANALYSIS_FREQUENCY === 0 &&
            recentActions.length >= MIN_ACTIONS_FOR_ANALYSIS) {
          await this.analyzeTimingPatterns(playerId);
        }

      } else {
        // Create new record
        const newRecord: ActionTimingRecord = {
          playerId,
          recentActions: [action],
          stats: this.calculateTimingStats([action]),
          botScore: 0,
          lastAnalyzed: Timestamp.now(),
        };

        await setDoc(timingRef, newRecord);
      }

    } catch (error) {
      console.error('❌ [BotDetect] Error recording action timing:', error);
    }
  }

  /**
   * Determine decision complexity based on action type and context
   */
  private getDecisionComplexity(
    actionType: string,
    thinkTimeMs: number,
    potSize?: number
  ): 'simple' | 'medium' | 'complex' {
    // Folds and checks are simple decisions
    if (actionType === 'fold' || actionType === 'check') {
      return 'simple';
    }

    // Calls are medium complexity
    if (actionType === 'call') {
      return 'medium';
    }

    // Raises and all-ins are complex
    return 'complex';
  }

  // ============================================================================
  // STATISTICS CALCULATION
  // ============================================================================

  /**
   * Calculate timing statistics from action history
   */
  private calculateTimingStats(actions: ActionTiming[]): TimingStats {
    if (actions.length === 0) {
      return {
        avgThinkTime: 0,
        stdDevThinkTime: 0,
        minThinkTime: 0,
        maxThinkTime: 0,
        actionsAnalyzed: 0,
        avgThinkTimeByAction: {},
        instantActionRate: 0,
        suspiciouslyFastRate: 0,
      };
    }

    const thinkTimes = actions.map(a => a.thinkTimeMs);
    
    // Basic stats
    const sum = thinkTimes.reduce((a, b) => a + b, 0);
    const avg = sum / thinkTimes.length;
    const min = Math.min(...thinkTimes);
    const max = Math.max(...thinkTimes);
    
    // Standard deviation
    const squaredDiffs = thinkTimes.map(t => Math.pow(t - avg, 2));
    const avgSquaredDiff = squaredDiffs.reduce((a, b) => a + b, 0) / squaredDiffs.length;
    const stdDev = Math.sqrt(avgSquaredDiff);

    // Average by action type
    const avgByAction: { [key: string]: number } = {};
    const actionGroups: { [key: string]: number[] } = {};
    
    for (const action of actions) {
      if (!actionGroups[action.actionType]) {
        actionGroups[action.actionType] = [];
      }
      actionGroups[action.actionType].push(action.thinkTimeMs);
    }

    for (const [actionType, times] of Object.entries(actionGroups)) {
      avgByAction[actionType] = times.reduce((a, b) => a + b, 0) / times.length;
    }

    // Suspicious rates
    const instantCount = thinkTimes.filter(t => t < INSTANT_ACTION_MS).length;
    const suspiciousFastCount = thinkTimes.filter(t => t < SUSPICIOUS_FAST_MS).length;
    
    return {
      avgThinkTime: avg,
      stdDevThinkTime: stdDev,
      minThinkTime: min,
      maxThinkTime: max,
      actionsAnalyzed: actions.length,
      avgThinkTimeByAction: avgByAction,
      instantActionRate: instantCount / actions.length,
      suspiciouslyFastRate: suspiciousFastCount / actions.length,
    };
  }

  // ============================================================================
  // BOT PATTERN ANALYSIS
  // ============================================================================

  /**
   * Analyze timing patterns for bot indicators
   */
  async analyzeTimingPatterns(playerId: string): Promise<number> {
    try {
      console.log(`🔍 [BotDetect] Analyzing timing for ${playerId.substring(0, 8)}`);

      const timingRef = doc(db, TIMINGS_COLLECTION, playerId);
      const timingDoc = await getDoc(timingRef);

      if (!timingDoc.exists()) {
        return 0;
      }

      const record = timingDoc.data() as ActionTimingRecord;
      const stats = record.stats;

      if (stats.actionsAnalyzed < MIN_ACTIONS_FOR_ANALYSIS) {
        console.log(`ℹ️ [BotDetect] Not enough actions (${stats.actionsAnalyzed})`);
        return 0;
      }

      let botScore = 0;
      const signals: string[] = [];
      const dataPoints: any[] = [];

      // Check 1: Suspiciously fast actions
      if (stats.suspiciouslyFastRate > SUSPICIOUS_FAST_RATE) {
        const points = Math.min(25, Math.round(stats.suspiciouslyFastRate * 100));
        botScore += points;
        signals.push('INHUMAN_REACTION_TIME');
        dataPoints.push({
          type: 'fast_actions',
          description: `${Math.round(stats.suspiciouslyFastRate * 100)}% of actions under ${SUSPICIOUS_FAST_MS}ms`,
          value: stats.suspiciouslyFastRate,
          weight: points,
        });
      }

      // Check 2: High instant action rate
      if (stats.instantActionRate > SUSPICIOUS_INSTANT_RATE) {
        const points = Math.min(20, Math.round(stats.instantActionRate * 50));
        botScore += points;
        signals.push('HIGH_INSTANT_RATE');
        dataPoints.push({
          type: 'instant_actions',
          description: `${Math.round(stats.instantActionRate * 100)}% of actions under ${INSTANT_ACTION_MS}ms`,
          value: stats.instantActionRate,
          weight: points,
        });
      }

      // Check 3: Suspiciously consistent timing (low variance)
      if (stats.stdDevThinkTime < ROBOT_CONSISTENT_VARIANCE && stats.actionsAnalyzed > 100) {
        botScore += 30;
        signals.push('ROBOTIC_CONSISTENCY');
        dataPoints.push({
          type: 'low_variance',
          description: `Standard deviation only ${Math.round(stats.stdDevThinkTime)}ms (robot-like)`,
          value: stats.stdDevThinkTime,
          weight: 30,
        });
      }

      // Check 4: Impossible minimum reaction time
      if (stats.minThinkTime < HUMAN_MIN_REACTION_MS) {
        botScore += 25;
        signals.push('IMPOSSIBLE_SPEED');
        dataPoints.push({
          type: 'min_time',
          description: `Minimum action time ${stats.minThinkTime}ms (below human limit)`,
          value: stats.minThinkTime,
          weight: 25,
        });
      }

      // Check 5: No variation between decision types
      // Humans take longer on complex decisions
      const avgByAction = stats.avgThinkTimeByAction;
      if (avgByAction.fold && avgByAction['all-in']) {
        const ratio = avgByAction['all-in'] / avgByAction.fold;
        if (ratio < 1.5) {
          // All-in decisions should take longer than folds
          botScore += 15;
          signals.push('NO_COMPLEXITY_VARIANCE');
          dataPoints.push({
            type: 'complexity_ratio',
            description: `All-in/fold time ratio: ${ratio.toFixed(2)} (should be higher)`,
            value: ratio,
            weight: 15,
          });
        }
      }

      // Normalize score to 0-100
      botScore = Math.min(100, botScore);

      console.log(`🤖 [BotDetect] Score: ${botScore}% (${signals.length} signals)`);

      // Update record with score
      await updateDoc(timingRef, {
        botScore,
        lastAnalyzed: Timestamp.now(),
      });

      // Create flag if threshold met
      if (botScore >= FLAG_THRESHOLDS.BOT_MEDIUM) {
        const hasExisting = await securityFlagService.hasExistingFlag(
          'bot',
          playerId,
          undefined,
          168 // 1 week
        );

        if (!hasExisting) {
          const severity = botScore >= FLAG_THRESHOLDS.BOT_HIGH ? 'high' : 'medium';

          await securityFlagService.createFlag({
            type: 'bot',
            severity,
            confidence: botScore,
            primaryUserId: playerId,
            evidence: {
              signals,
              dataPoints,
              summary: `Bot score ${botScore}%: ${signals.join(', ')}`,
            },
          });
        }
      }

      return botScore;

    } catch (error) {
      console.error('❌ [BotDetect] Error analyzing timing patterns:', error);
      return 0;
    }
  }

  /**
   * Get current bot score for a player
   */
  async getBotScore(playerId: string): Promise<number> {
    try {
      const timingRef = doc(db, TIMINGS_COLLECTION, playerId);
      const timingDoc = await getDoc(timingRef);

      if (timingDoc.exists()) {
        return (timingDoc.data() as ActionTimingRecord).botScore;
      }

      return 0;

    } catch (error) {
      console.error('❌ [BotDetect] Error getting bot score:', error);
      return 0;
    }
  }

  /**
   * Get timing statistics for a player
   */
  async getTimingStats(playerId: string): Promise<TimingStats | null> {
    try {
      const timingRef = doc(db, TIMINGS_COLLECTION, playerId);
      const timingDoc = await getDoc(timingRef);

      if (timingDoc.exists()) {
        return (timingDoc.data() as ActionTimingRecord).stats;
      }

      return null;

    } catch (error) {
      console.error('❌ [BotDetect] Error getting timing stats:', error);
      return null;
    }
  }

  /**
   * Get full timing record for a player (for admin review)
   */
  async getTimingRecord(playerId: string): Promise<ActionTimingRecord | null> {
    try {
      const timingRef = doc(db, TIMINGS_COLLECTION, playerId);
      const timingDoc = await getDoc(timingRef);

      if (timingDoc.exists()) {
        return timingDoc.data() as ActionTimingRecord;
      }

      return null;

    } catch (error) {
      console.error('❌ [BotDetect] Error getting timing record:', error);
      return null;
    }
  }

  /**
   * Force re-analysis of a player
   */
  async forceReanalyze(playerId: string): Promise<number> {
    return this.analyzeTimingPatterns(playerId);
  }

  /**
   * Clear timing history for a player (for testing)
   */
  async clearTimingHistory(playerId: string): Promise<void> {
    try {
      const timingRef = doc(db, TIMINGS_COLLECTION, playerId);
      const emptyRecord: ActionTimingRecord = {
        playerId,
        recentActions: [],
        stats: this.calculateTimingStats([]),
        botScore: 0,
        lastAnalyzed: Timestamp.now(),
      };
      await setDoc(timingRef, emptyRecord);

    } catch (error) {
      console.error('❌ [BotDetect] Error clearing timing history:', error);
    }
  }

  /**
   * Get action timing when turn starts (for calculating think time)
   * Returns current timestamp in ms
   */
  getTurnStartTime(): number {
    return Date.now();
  }

  /**
   * Calculate think time from turn start
   */
  calculateThinkTime(turnStartTime: number): number {
    return Date.now() - turnStartTime;
  }
}

// ============================================================================
// SINGLETON EXPORT
// ============================================================================

const botDetectionService = new BotDetectionService();
export { botDetectionService };
export default botDetectionService;